import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useSortState from '@/hooks/useSortState';
import { PaginatedResponse, VideoFactoryProject } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import DraftForm from './DraftForm';
import HistoryTableRow from './HistoryTableRow';

const StyledTable = styled(Table)`
  td,
  th {
    font-weight: 700;
    &:nth-child(2) {
      min-width: 240px;
    }
    &:nth-child(3) {
      min-width: 170px;
    }
    &:nth-child(4) {
      min-width: 140px;
    }
    &:nth-child(5) {
      min-width: 140px;
    }
    &:nth-child(6) {
      min-width: 140px;
    }
    &:nth-child(7) {
      min-width: 140px;
    }
    &:nth-child(8) {
      min-width: 160px;
    }
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

const History: FC = () => {
  const [searchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('projectId') || '');

  const [showDraftModal, setShowDraftModal] = useState(
    !!searchParams.get('projectId') && !!searchParams.get('draft'),
  );

  const debouncedSearch = useDebounce(search, 500);

  const { sort, setSort, sortQueryParamObj } = useSortState({
    property: 'created_at',
    direction: SortDirection.DESC,
  });

  const fetchProjects = ({ pageParam = '/v1/video-factory/projects' }) => {
    return api.get<never, PaginatedResponse<VideoFactoryProject>>(pageParam, {
      params: {
        ...(!!sortQueryParamObj && sortQueryParamObj),
        ...(!!debouncedSearch && {
          'filter[search]': debouncedSearch,
        }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<{ data: VideoFactoryProject[]; next_page_url?: string }>(
    {
      queryKey: [
        'video_factory_projects',
        {
          sort,
          ...(!!debouncedSearch && {
            search: debouncedSearch,
          }),
        },
      ],
      queryFn: fetchProjects,
      getNextPageParam: (lastPage) => {
        return lastPage.next_page_url;
      },
      refetchInterval: 30_000,
    },
  );

  const projects = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<VideoFactoryProject[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by Topic"
      />
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Video</Th>
              <Th>Topic</Th>
              <SortableTh
                title="Status"
                sort={sort}
                setSort={setSort}
                property="status"
              />
              <SortableTh
                title="Created At"
                sort={sort}
                setSort={setSort}
                property="created_at"
              />
              <SortableTh
                title="Completed At"
                sort={sort}
                setSort={setSort}
                property="finished_at"
              />
              <Th>Story Prompt Version</Th>
              <Th>Script Prompt Version</Th>
              <Th>Error</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!projects?.length}
            >
              {projects?.map((project) => (
                <HistoryTableRow project={project} key={project.id} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
      <AnimatePresence>
        {showDraftModal && !!searchParams.get('projectId') && (
          <DraftForm
            onClose={() => setShowDraftModal(false)}
            projectId={searchParams.get('projectId')!}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default History;
